// SideNavBar.jsx
import React from 'react';
import {
	Box,
	Drawer,
	List,
	ListItem,
	IconButton,
	Stack,
	useMediaQuery,
	useTheme,
	Chip,
	Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useIsAuthenticated } from '@azure/msal-react';
import { SignInButton } from '@/components/buttons/SignInButton';
import { SignOutButton } from '@/components/buttons/SignOutButton';
import { InvertSideNavColorsButton } from '@/components/buttons/InvertSideNavColorsButton';
import { useDispatch, useSelector } from 'react-redux';
import { toggleOpen, toggleWidth } from '@/features/sideNav/sideNavSlice';
import { IS_DEV, IS_PRE } from '@/utils/environment';
import { useNavVariant } from '@/components/layouts/navVariants';
import { useAdminModeContext } from '@/context/adminModeContext';
import { ADMIN_MODE_BANNER_HEIGHT } from '@/components/layouts/AdminModeBanner';
import { useSideNavStyle } from './useSideNavStyle';

export const SideNavBar = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { open, width } = useSelector((state) => state.sideNavSlice);
	const { collapsed, isLightMode, inverted, sideNavItemColor } = useSideNavStyle();
	const sideNavBg = inverted ? theme.palette.background.default : theme.palette.sideNav;

	const { Brand, BrandCaption, Sections } = useNavVariant();
	const { isAdminMode } = useAdminModeContext();
	const bannerHeight = isAdminMode ? ADMIN_MODE_BANNER_HEIGHT : 0;

	const handleDrawerToggle = () => dispatch(toggleOpen());
	const handleDrawerWidthToggle = () => {
		if (isMobile) {
			return handleDrawerToggle();
		}
		dispatch(toggleWidth());
	};

	const isAuthenticated = useIsAuthenticated();

	return (
		<>
			<Drawer
				className='side_nav_bar'
				sx={{
					width: width,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: width,
						boxSizing: 'border-box',
						border: 'none',
						backgroundColor: sideNavBg,
						overflowX: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						top: bannerHeight,
						height: `calc(100% - ${bannerHeight}px)`,
						transition: theme.transitions.create(['width'], {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.leavingScreen,
						}),
						...(inverted && {
							borderRight: `1px solid ${theme.palette.divider}`,
						}),
					},
					'& .MuiListItem-root': {
						padding: collapsed ? 0 : '0 16px',
					},
				}}
				variant={isMobile ? 'temporary' : 'permanent'}
				open={isMobile ? open : true}
				onClose={handleDrawerToggle}
				anchor='left'
			>
				{collapsed ? (
					<IconButton
						variant='outlined'
						onClick={handleDrawerWidthToggle}
						sx={{ mb: '0.5em' }}
					>
						<MenuIcon sx={{ color: sideNavItemColor }} />
					</IconButton>
				) : (
					<Box sx={{ ml: '20px', mb: '0.5em', mr: '20px' }}>
						<Stack
							direction='row'
							justifyContent={'space-between'}
							alignContent={'start'}
							spacing={0.5}
							width='100%'
							pt={1.25}
							pb={1.5}
						>
							<Stack
								direction='row'
								alignItems='center'
								spacing={1}
								justifyContent={'space-between'}
								width='100%'
							>
								<Brand />
								{IS_DEV && <Chip label='DEV' color='primary' size='small' />}
								{IS_PRE && <Chip label='PRE' color='secondary' size='small' />}
							</Stack>
							<IconButton
								variant='outlined'
								onClick={handleDrawerWidthToggle}
								sx={{ p: '4px' }}
							>
								<KeyboardArrowLeftIcon sx={{ color: sideNavItemColor }} />
							</IconButton>
						</Stack>
						{BrandCaption && <BrandCaption />}
					</Box>
				)}

				<Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
					{Sections && <Sections />}
				</Box>

				{isLightMode && (
					<Box sx={{ mx: collapsed ? 0 : '24px', my: '4px' }}>
						<InvertSideNavColorsButton color={sideNavItemColor} />
					</Box>
				)}

				<Divider
					sx={{
						borderColor: inverted
							? 'rgba(28, 37, 54, 0.12)'
							: 'rgba(250, 253, 255, 0.12)',
					}}
				/>
				<List disablePadding sx={{ flexShrink: 0 }}>
					<ListItem disablePadding disableGutters={collapsed}>
						{isAuthenticated ? (
							<SignOutButton color={sideNavItemColor} />
						) : (
							<SignInButton showListItem color={sideNavItemColor} />
						)}
					</ListItem>
				</List>
			</Drawer>
		</>
	);
};
